import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-pricing',
  templateUrl: './pricing.component.html',
  styleUrls: ['./pricing.component.scss']
})
export class PricingComponent implements OnInit, OnDestroy {
  month = '';
  isLoading = false;
  error = '';
  data: any = null;
  activeChart = '';
  activeIndex = -1;
  dayPinned = false;
  private requestSub: Subscription;

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) { }

  ngOnDestroy(): void {
    if (this.requestSub) {
      this.requestSub.unsubscribe();
    }
  }

  ngOnInit(): void {
    this.month = this.currentMonth();
    this.load();
  }

  currentMonth(): string {
    const now = new Date();
    const m = now.getMonth() + 1;
    return now.getFullYear() + '-' + (m < 10 ? '0' + m : '' + m);
  }

  onMonthChange(): void {
    this.load();
  }

  load(): void {
    if (!this.month) {
      return;
    }
    if (this.requestSub) {
      this.requestSub.unsubscribe();
    }
    const requestedMonth = this.month;
    this.isLoading = true;
    this.error = '';
    this.data = null;
    const headers = new HttpHeaders(this.authService.getAuthHeaders());
    const params = new HttpParams().set('month', requestedMonth);
    this.requestSub = this.http.get(environment.apiUrl + '/v2', { headers: headers, params: params }).subscribe(
      (response) => {
        if (requestedMonth !== this.month) {
          return;
        }
        this.data = response;
        this.isLoading = false;
      },
      () => {
        if (requestedMonth !== this.month) {
          return;
        }
        this.data = null;
        this.error = 'Failed to load pricing for ' + requestedMonth + '.';
        this.isLoading = false;
      }
    );
  }

  usd(n: number): string {
    if (n === null || n === undefined || isNaN(n)) {
      return '$0.00';
    }
    return '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  pct(part: number, whole: number): string {
    if (!whole) {
      return '0.0%';
    }
    return ((100 * part) / whole).toFixed(1) + '%';
  }

  maxDaily(series: any): number {
    if (!series) {
      return 1;
    }
    let max = 0;
    const keys = ['production', 'staging', 'sandbox', 'untagged'];
    const len = (series.production && series.production.length) ? series.production.length : 0;
    for (let i = 0; i < len; i++) {
      let sum = 0;
      for (let k = 0; k < keys.length; k++) {
        const arr = series[keys[k]];
        sum += (arr && arr[i]) ? arr[i] : 0;
      }
      if (sum > max) {
        max = sum;
      }
    }
    return max > 0 ? max : 1;
  }

  maxList(values: number[]): number {
    if (!values || !values.length) {
      return 1;
    }
    let max = 0;
    for (let i = 0; i < values.length; i++) {
      if (values[i] > max) {
        max = values[i];
      }
    }
    return max > 0 ? max : 1;
  }

  heightPct(value: number, max: number): string {
    const n = value > 0 ? value : 0;
    return ((100 * n) / max).toFixed(1) + '%';
  }

  showDayTick(index: number, count: number): boolean {
    if (count <= 10) {
      return true;
    }
    if (index === 0 || index === count - 1) {
      return true;
    }
    return index % 5 === 0;
  }

  dayNum(label: string): string {
    if (!label) {
      return '';
    }
    const parts = String(label).split(' ');
    return parts[parts.length - 1];
  }

  showDay(chart: string, index: number): void {
    if (this.dayPinned) {
      return;
    }
    this.activeChart = chart;
    this.activeIndex = index;
  }

  hideDay(chart: string): void {
    if (this.dayPinned || this.activeChart !== chart) {
      return;
    }
    this.activeChart = '';
    this.activeIndex = -1;
  }

  toggleDay(chart: string, index: number): void {
    if (this.dayPinned && this.activeChart === chart && this.activeIndex === index) {
      this.dayPinned = false;
      this.activeChart = '';
      this.activeIndex = -1;
      return;
    }
    this.dayPinned = true;
    this.activeChart = chart;
    this.activeIndex = index;
  }

  stillUntagged(): number {
    if (!this.data || !this.data.ceUntagged) {
      return 0;
    }
    let sum = 0;
    for (let i = 0; i < this.data.ceUntagged.length; i++) {
      sum += this.rowAmount(this.data.ceUntagged[i]);
    }
    return sum;
  }

  exportFileName(): string {
    return 'AWS-cost-by-environment-' + (this.month || 'month') + '.pdf';
  }

  monthLabel(): string {
    const names = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    if (!this.month || this.month.indexOf('-') < 0) {
      return this.month || '';
    }
    const parts = this.month.split('-');
    const idx = parseInt(parts[1], 10) - 1;
    return (names[idx] || parts[1]) + ' ' + parts[0];
  }

  exportPdf(): void {
    if (!this.data) {
      return;
    }
    const pdf = buildHandoffPdf(this.reportItems());
    const blob = new Blob([pdf], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = this.exportFileName();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  private reportItems(): PdfItem[] {
    const items: PdfItem[] = [];
    const data = this.data;
    const account = data.account;
    const total = account.total;
    function add(kind: string, text: string) {
      items.push({ kind: kind, text: text });
    }
    function note(text: string) {
      const lines = wrapPdfText(text, 100);
      for (let i = 0; i < lines.length; i++) {
        add('note', lines[i]);
      }
    }
    function table(headers: string[], body: string[][], widths: number[], numericFrom: number) {
      add('monoBold', formatPdfRow(headers, widths, numericFrom));
      for (let i = 0; i < body.length; i++) {
        add('mono', formatPdfRow(body[i], widths, numericFrom));
      }
    }
    add('title', 'AWS cost by environment');
    add('heading', this.monthLabel());
    note('This page shows AWS cost by allocation tags (Environment and AICost), and by environment and region.');
    note('Account 299036201564. ' + data.start + ' to ' + data.endExclusive + ' (exclusive end), ' + (data.estimated ? 'estimated' : 'finalized') + '.');
    add('gap', '');
    add('note', 'Sandbox      ' + this.usd(account.sandbox) + '    ' + this.pct(account.sandbox, total) + ' of ' + this.usd(total));
    add('note', 'Staging      ' + this.usd(account.staging) + '    ' + this.pct(account.staging, total) + ' of ' + this.usd(total));
    add('note', 'Production   ' + this.usd(account.production) + '    ' + this.pct(account.production, total) + ' of ' + this.usd(total));
    add('note', 'AI Cost      ' + this.usd(this.aiCost()) + '    ' + this.pct(this.aiCost(), total) + ' of ' + this.usd(total));
    note('AI Cost sits inside the environment totals. It is not added on top of them.');
    add('note', 'Month total ' + this.usd(total) + '. Untagged ' + this.usd(account.untagged) + '. Still untagged ' + this.usd(this.stillUntagged()) + '.');
    note('us-east-2 is production infra (RDS, DocumentDB, ElastiCache, EC2, DMS). us-east-1 is almost all Bedrock. AICost=true is only in us-east-1.');
    note('VPC addresses, CloudWatch, QuickSight, WAF, and backup storage will stay untagged because those charges do not carry the resource tag.');

    const regionWidths = [22, 14, 14, 14, 14, 14];
    const regionHead = ['Region', 'production', 'staging', 'sandbox', 'untagged', 'Total'];
    add('heading', 'Environment by region (UnblendedCost, USD)');
    const regionBody = [];
    const material = this.materialRegions();
    for (let i = 0; i < material.length; i++) {
      const row = material[i];
      regionBody.push([row.region, this.usd(row.production), this.usd(row.staging), this.usd(row.sandbox), this.usd(row.untagged), this.usd(row.total)]);
    }
    regionBody.push(['Account (all regions)', this.usd(account.production), this.usd(account.staging), this.usd(account.sandbox), this.usd(account.untagged), this.usd(account.total)]);
    table(regionHead, regionBody, regionWidths, 1);
    const small = this.smallRegions();
    if (small.length) {
      add('note', 'Regions under $100 (' + small.length + ', ' + this.usd(this.smallRegionTotal()) + ')');
      const smallBody = [];
      for (let i = 0; i < small.length; i++) {
        const row = small[i];
        smallBody.push([row.region, this.usd(row.production), this.usd(row.staging), this.usd(row.sandbox), this.usd(row.untagged), this.usd(row.total)]);
      }
      table(regionHead, smallBody, regionWidths, 1);
    }
    note('Source: Cost Explorer UnblendedCost. Every region with spend, plus us-east-1 and us-east-2.');

    add('heading', 'AICost by region (UnblendedCost, USD)');
    const aiHead = ['Region', 'AICost=true', 'AICost empty', 'Total', 'AI share'];
    const aiWidths = [16, 14, 14, 14, 12];
    const aiBody = [];
    for (let i = 0; i < material.length; i++) {
      const row = material[i];
      aiBody.push([row.region, this.usd(row.aiTrue), this.usd(row.aiEmpty), this.usd(row.total), this.pct(row.aiTrue, row.total)]);
    }
    table(aiHead, aiBody, aiWidths, 1);
    if (small.length) {
      add('note', 'Regions under $100 (' + small.length + ', ' + this.usd(this.smallRegionTotal()) + ')');
      const smallAi = [];
      for (let i = 0; i < small.length; i++) {
        const row = small[i];
        smallAi.push([row.region, this.usd(row.aiTrue), this.usd(row.aiEmpty), this.usd(row.total), this.pct(row.aiTrue, row.total)]);
      }
      table(aiHead, smallAi, aiWidths, 1);
    }

    this.pushPairTable(items, 'Environment x AICost x region', ['Region', 'Environment', 'AICost', 'Unblended $'], [18, 16, 12, 16], data.envAi, 3);
    this.pushPairTable(items, 'AICost=true services, us-east-1', ['Region', 'Service', 'Unblended $'], [16, 52, 16], data.aiServices, 2);
    if (data.ue1Days && data.ue1Daily) {
      this.pushDailyTable(items, 'us-east-1 daily Environment (UnblendedCost, USD)', data.ue1Days, data.ue1Daily);
      note('Source: Cost Explorer UnblendedCost, us-east-1, each day of the month.');
    }
    if (data.ue2Days && data.ue2Daily) {
      this.pushDailyTable(items, 'us-east-2 daily Environment (UnblendedCost, USD)', data.ue2Days, data.ue2Daily);
      note('Source: Cost Explorer UnblendedCost, us-east-2, each day of the month.');
    }
    this.pushAiDaily(items, data.aiDailyDays, data.aiDaily);
    this.pushPairTable(items, 'Largest us-east-1 services by Environment', ['Service', 'Environment', 'Unblended $'], [52, 16, 16], data.ue1Services, 2);
    this.pushPairTable(items, 'Largest us-east-2 services by Environment', ['Service', 'Environment', 'Unblended $'], [52, 16, 16], data.ue2Services, 2);

    add('heading', 'Cost Explorer untagged dollars');
    note('Still untagged in the last 7 days, summed for the month. Earlier untagged charges on resources that now have an Environment tag are omitted.');
    note('VPC addresses, CloudWatch, QuickSight, WAF, and backup storage will stay untagged because those charges do not carry the resource tag.');
    this.pushPairTable(items, '', ['Region', 'Service', 'Unblended $'], [16, 52, 16], data.ceUntagged, 2);
    return items;
  }

  private pushPairTable(items: PdfItem[], title: string, headers: string[], widths: number[], rows: any[], amountIndex: number): void {
    const shown = this.visibleRows(rows);
    const hidden = this.hiddenRows(rows);
    if (!shown.length && !hidden.length) {
      return;
    }
    if (title) {
      items.push({ kind: 'heading', text: title });
    }
    if (shown.length) {
      items.push({ kind: 'monoBold', text: formatPdfRow(headers, widths, headers.length - 1) });
      for (let i = 0; i < shown.length; i++) {
        items.push({ kind: 'mono', text: formatPdfRow(this.pairCells(shown[i], amountIndex), widths, headers.length - 1) });
      }
    }
    if (hidden.length) {
      items.push({ kind: 'note', text: 'Under $100 (' + hidden.length + ', ' + this.usd(this.hiddenTotal(rows)) + ')' });
      items.push({ kind: 'monoBold', text: formatPdfRow(headers, widths, headers.length - 1) });
      for (let i = 0; i < hidden.length; i++) {
        items.push({ kind: 'mono', text: formatPdfRow(this.pairCells(hidden[i], amountIndex), widths, headers.length - 1) });
      }
    }
  }

  private pairCells(row: any[], amountIndex: number): string[] {
    const cells = [];
    for (let i = 0; i < row.length; i++) {
      cells.push(i === amountIndex ? this.usd(row[i]) : String(row[i]));
    }
    return cells;
  }

  private pushDailyTable(items: PdfItem[], title: string, days: string[], series: any): void {
    if (!days || !series) {
      return;
    }
    items.push({ kind: 'heading', text: title });
    const headers = ['Day', 'production', 'staging', 'sandbox', 'untagged', 'Total'];
    const widths = [10, 14, 14, 14, 14, 14];
    items.push({ kind: 'monoBold', text: formatPdfRow(headers, widths, 1) });
    for (let i = 0; i < days.length; i++) {
      const production = series.production[i] || 0;
      const staging = series.staging[i] || 0;
      const sandbox = series.sandbox[i] || 0;
      const untagged = series.untagged[i] || 0;
      items.push({ kind: 'mono', text: formatPdfRow([
        days[i],
        this.usd(production),
        this.usd(staging),
        this.usd(sandbox),
        this.usd(untagged),
        this.usd(production + staging + sandbox + untagged)
      ], widths, 1) });
    }
  }

  private pushAiDaily(items: PdfItem[], days: string[], values: number[]): void {
    if (!days || !values || !values.length) {
      return;
    }
    items.push({ kind: 'heading', text: 'Daily AICost=true, us-east-1 (UnblendedCost, USD)' });
    const headers = ['Day', 'AI Cost'];
    const widths = [12, 16];
    items.push({ kind: 'monoBold', text: formatPdfRow(headers, widths, 1) });
    for (let i = 0; i < days.length; i++) {
      items.push({ kind: 'mono', text: formatPdfRow([days[i], this.usd(values[i] || 0)], widths, 1) });
    }
  }

  share(part: number, whole: number): number {
    if (!whole) {
      return 0;
    }
    return (100 * part) / whole;
  }

  isMaterial(n: number): boolean {
    if (n === null || n === undefined || isNaN(n)) {
      return false;
    }
    return Math.abs(n) >= 100;
  }

  materialRegions(): any[] {
    return this.regionsBySize(true);
  }

  smallRegions(): any[] {
    return this.regionsBySize(false);
  }

  smallRegionTotal(): number {
    const rows = this.smallRegions();
    let sum = 0;
    for (let i = 0; i < rows.length; i++) {
      sum += rows[i].total || 0;
    }
    return sum;
  }

  visibleRows(rows: any[]): any[] {
    return this.rowsBySize(rows, true);
  }

  hiddenRows(rows: any[]): any[] {
    return this.rowsBySize(rows, false);
  }

  hiddenTotal(rows: any[]): number {
    const hidden = this.hiddenRows(rows);
    let sum = 0;
    for (let i = 0; i < hidden.length; i++) {
      sum += this.rowAmount(hidden[i]);
    }
    return sum;
  }

  private regionsBySize(material: boolean): any[] {
    const out = [];
    if (!this.data || !this.data.regions) {
      return out;
    }
    for (let i = 0; i < this.data.regions.length; i++) {
      const row = this.data.regions[i];
      if (this.isMaterial(row.total) === material) {
        out.push(row);
      }
    }
    return out;
  }

  private rowsBySize(rows: any[], material: boolean): any[] {
    const out = [];
    if (!rows) {
      return out;
    }
    for (let i = 0; i < rows.length; i++) {
      if (this.isMaterial(this.rowAmount(rows[i])) === material) {
        out.push(rows[i]);
      }
    }
    return out;
  }

  private rowAmount(row: any): number {
    if (!row || !row.length) {
      return 0;
    }
    const value = row[row.length - 1];
    return typeof value === 'number' ? value : 0;
  }

  aiCost(): number {
    if (!this.data) {
      return 0;
    }
    if (this.data.regions && this.data.regions.length) {
      let sum = 0;
      for (let i = 0; i < this.data.regions.length; i++) {
        sum += this.data.regions[i].aiTrue || 0;
      }
      return sum;
    }
    return (this.data.ue1 && this.data.ue1.aiTrue) ? this.data.ue1.aiTrue : 0;
  }

  regionShare(value: number, regionTotal: number): string {
    return this.pct(value, regionTotal);
  }
}

interface PdfItem {
  kind: string;
  text: string;
}

function wrapPdfText(text: string, width: number): string[] {
  const words = text.split(' ');
  const lines = [];
  let current = '';
  for (let i = 0; i < words.length; i++) {
    const next = current ? current + ' ' + words[i] : words[i];
    if (next.length > width && current) {
      lines.push(current);
      current = words[i];
    } else {
      current = next;
    }
  }
  if (current) {
    lines.push(current);
  }
  return lines;
}

function pdfEscape(text: string): string {
  return String(text).replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)');
}

function formatPdfRow(cells: string[], widths: number[], numericFrom: number): string {
  let line = '';
  for (let i = 0; i < cells.length; i++) {
    line += pdfPad(cells[i] || '', widths[i] || 12, i >= numericFrom);
  }
  return line;
}

function pdfPad(text: string, width: number, right: boolean): string {
  let value = String(text);
  if (value.length > width) {
    value = value.slice(0, width);
  }
  while (value.length < width) {
    value = right ? ' ' + value : value + ' ';
  }
  return value;
}

function buildHandoffPdf(items: PdfItem[]): string {
  const pages: string[][] = [[]];
  let y = 752;
  let lastHeader = '';
  function page() {
    return pages[pages.length - 1];
  }
  function nextPage() {
    pages.push([]);
    y = 752;
  }
  function place(text: string, size: number, font: string, drop: number) {
    if (y - drop < 46) {
      nextPage();
      if (font === 'F3' && lastHeader) {
        page().push(drawPdfText(lastHeader, 36, y, 8, 'F4'));
        y -= 11;
      }
    }
    page().push(drawPdfText(text, 36, y, size, font));
    y -= drop;
  }
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.kind === 'gap') {
      y -= 8;
      continue;
    }
    if (item.kind === 'title') {
      place(item.text, 16, 'F2', 22);
    } else if (item.kind === 'heading') {
      if (y < 140) {
        nextPage();
      }
      y -= 6;
      place(item.text, 12, 'F2', 18);
    } else if (item.kind === 'note') {
      place(item.text, 9, 'F1', 12);
    } else if (item.kind === 'monoBold') {
      lastHeader = item.text;
      place(item.text, 8, 'F4', 11);
    } else {
      place(item.text, 8, 'F3', 11);
    }
  }
  const streams = [];
  for (let p = 0; p < pages.length; p++) {
    const footer = drawPdfText('Page ' + (p + 1) + ' of ' + pages.length, 36, 28, 8, 'F1');
    streams.push(pages[p].join('\n') + '\n' + footer);
  }
  return assemblePdf(streams);
}

function drawPdfText(text: string, x: number, y: number, size: number, font: string): string {
  return 'BT\n/' + font + ' ' + size + ' Tf\n1 0 0 1 ' + x + ' ' + y + ' Tm\n(' + pdfEscape(text) + ') Tj\nET';
}

function assemblePdf(streams: string[]): string {
  const objects = [];
  objects.push('1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj\n');
  let kids = '';
  for (let i = 0; i < streams.length; i++) {
    kids += (7 + i * 2) + ' 0 R ';
  }
  objects.push('2 0 obj\n<< /Type /Pages /Count ' + streams.length + ' /Kids [' + kids + '] >>\nendobj\n');
  objects.push('3 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>\nendobj\n');
  objects.push('4 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>\nendobj\n');
  objects.push('5 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Courier >>\nendobj\n');
  objects.push('6 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Courier-Bold >>\nendobj\n');
  for (let i = 0; i < streams.length; i++) {
    const pageNum = 7 + i * 2;
    const contentNum = pageNum + 1;
    objects.push(pageNum + ' 0 obj\n<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Contents ' + contentNum + ' 0 R /Resources << /Font << /F1 3 0 R /F2 4 0 R /F3 5 0 R /F4 6 0 R >> >> >>\nendobj\n');
    objects.push(contentNum + ' 0 obj\n<< /Length ' + streams[i].length + ' >>\nstream\n' + streams[i] + '\nendstream\nendobj\n');
  }
  let header = '%PDF-1.4\n';
  let body = '';
  const offsets = [0];
  let cursor = header.length;
  for (let j = 0; j < objects.length; j++) {
    offsets.push(cursor);
    body += objects[j];
    cursor += objects[j].length;
  }
  let xref = 'xref\n0 ' + (objects.length + 1) + '\n0000000000 65535 f \n';
  for (let k = 1; k <= objects.length; k++) {
    let off = String(offsets[k]);
    while (off.length < 10) {
      off = '0' + off;
    }
    xref += off + ' 00000 n \n';
  }
  const trailer = 'trailer\n<< /Size ' + (objects.length + 1) + ' /Root 1 0 R >>\nstartxref\n' + cursor + '\n%%EOF\n';
  return header + body + xref + trailer;
}
